import {
  useCreateCourseMutation,
  useDeleteCourseMutation,
  useGetCourseQuery,
  useUpdateCourseMutation,
} from "@api/course";
import { useRegenerateAllEmbeddingsMutation } from "@api/material";
import { useGetUniversitiesQuery } from "@api/university";
import Header from "@components/Header";
import { IconWithTooltip } from "@components/IconWithTooltip";
import { ModalVariant, useModal } from "@components/Modal";
import {
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from "@heroicons/react/16/solid";
import { useIsUserRole, useUserData } from "@hooks/auth";
import { useOnChangeBlocker, useToastEffect } from "@hooks/hooks";
import { MAX_CHAT_PROMPT_LENGTH } from "@onetutor/shared/constant";
import { CreateCourseDTO } from "@onetutor/shared/dto";
import { ChatModels, CourseFeatures, ModelTypes } from "@onetutor/shared/type";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { validate as validateUUID } from "uuid";
import { showToast } from "../../app/store/statusSlice";
import CitationToggle from "./components/CitationToggle";
import CourseFeatureToggle from "./components/CourseFeatureToggle";
import PasswordToggle from "./components/PasswordToggle";

export function CourseManagementPage() {
  const dispatch = useDispatch();
  const managerInputRef = useRef<HTMLInputElement>(null);
  const { user } = useUserData();
  if (!user) {
    throw new Error("User must be logged in to access this page");
  }

  const courseNameId = useId();
  const aiModelId = useId();
  const courseDescriptionId = useId();
  const courseDescriptionInfoId = useId();
  const externalLinkId = useId();
  const newManagerId = useId();
  const universityId = useId();
  const chatPromptId = useId();
  const lecturerIdInputId = useId();

  const { Modal: DeleteModal, openModal: openDeleteModal } = useModal();
  const { Modal: RegenerateModal, openModal: openRegenerateModal } = useModal();

  const initialCourse: CreateCourseDTO = {
    name: "",
    modelType: ModelTypes.GPT_4O,
    description: "",
    includeDescriptionInChat: true,
    extLink: null,
    chapters: [],
    managerIds: [user.id],
    lecturerId: user.id,
    password: null,
    universityId: null,
    citationsEnabled: true,
    chatPrompt: null,
    isResearchProject: undefined,
    enabledFeatures: [CourseFeatures.CHAT, CourseFeatures.QUIZ],
  };

  const [searchParams] = useSearchParams();
  const isEditing = searchParams.get("edit") === "true";
  const { courseId } = useParams();

  const { t } = useTranslation(["courseManagementPage", "toast", "commons"]);
  const isAdmin = useIsUserRole("admin");
  const { data: courseData } = useGetCourseQuery(courseId ?? "", {
    skip: !isEditing,
  });
  const [course, setCourse] = useState<CreateCourseDTO>(initialCourse);
  const { data: universities } = useGetUniversitiesQuery();
  const availableModels = ChatModels.getAll();

  const navigate = useNavigate();

  const [
    createCourse,
    {
      isLoading: createLoading,
      isError: createIsError,
      isSuccess: createIsSuccess,
      error: createError,
    },
  ] = useCreateCourseMutation();

  const [
    updateCourse,
    {
      isLoading: updateLoading,
      isError: updateIsError,
      isSuccess: updateIsSuccess,
      error: updateError,
    },
  ] = useUpdateCourseMutation();

  const [
    deleteCourse,
    {
      isLoading: deleteLoading,
      isSuccess: deleteSuccess,
      isError: deleteIsError,
      error: deleteError,
    },
  ] = useDeleteCourseMutation();

  const [
    regenerateAllEmbeddings,
    {
      isError: regenerateIsError,
      isSuccess: regenerateSuccess,
      error: regenerateError,
    },
  ] = useRegenerateAllEmbeddingsMutation();

  useToastEffect({
    isError: deleteIsError,
    isSuccess: deleteSuccess,
    error: deleteError,
    successMessage: t("courseManagementPage.successDeleteCourse", {
      ns: "toast",
    }),
  });

  useToastEffect({
    isError: updateIsError,
    isSuccess: updateIsSuccess,
    error: updateError,
    successMessage: t("courseManagementPage.successEditCourse", {
      ns: "toast",
    }),
  });

  useToastEffect({
    isError: createIsError,
    isSuccess: createIsSuccess,
    error: createError,
    successMessage: t("courseManagementPage.successCreateCourse", {
      ns: "toast",
    }),
  });
  useToastEffect({
    isError: regenerateIsError,
    isSuccess: regenerateSuccess,
    successMessage: t("courseManagementPage.successRegenerateAllEmbeddings", {
      ns: "toast",
    }),
    error: regenerateError,
  });

  const initialRef = useRef<CreateCourseDTO>(initialCourse);
  useEffect(() => {
    if (!courseData) {
      return;
    }

    const formattedCourse = {
      ...courseData,
      password: courseData.isPasswordProtected ? undefined : null,
      lecturerId: courseData.lecturer.id,
      universityId: courseData.university?.id || null,
      enabledFeatures: courseData.enabledFeatures || [
        CourseFeatures.CHAT,
        CourseFeatures.QUIZ,
      ],
    };

    initialRef.current = formattedCourse;
    setCourse(formattedCourse);
  }, [courseData]);

  useOnChangeBlocker(
    initialRef,
    course,
    createIsSuccess || updateIsSuccess || deleteSuccess,
  );

  useEffect(() => {
    if (
      !isAdmin &&
      !isEditing &&
      universities &&
      universities.length &&
      !course.universityId
    ) {
      setCourse((prev) => ({
        ...prev,
        universityId: universities[0].id,
      }));
    }
  }, [isEditing, universities, course.universityId, isAdmin]);

  const handleAddManager = (e: React.UIEvent) => {
    e.preventDefault();
    const input = managerInputRef.current;
    if (!input) {
      return;
    }

    const newManagerId = input.value.trim();
    if (newManagerId === "") {
      return;
    }

    if (!validateUUID(newManagerId)) {
      input.setCustomValidity(t("invalidManagerId"));
      input.reportValidity();
      return;
    }

    if (course.managerIds.includes(newManagerId)) {
      input.setCustomValidity(t("managerAlreadyAdded"));
      input.reportValidity();
      return;
    }

    setCourse((prev) => ({
      ...prev,
      managerIds: [...prev.managerIds, newManagerId],
    }));
    input.value = "";
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const courseToSubmit: CreateCourseDTO = {
      ...course,
      chatPrompt: course.chatPrompt?.trim() || null,
      password:
        typeof course.password === "string"
          ? course.password.trim()
          : course.password,
      extLink: course.extLink?.trim() || null,
      isResearchProject: isAdmin ? course.isResearchProject : undefined,
    };

    if (isEditing && courseId) {
      updateCourse({ course: courseToSubmit, courseId });
    } else {
      createCourse(courseToSubmit);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/profile`);
    dispatch(
      showToast({
        message: t("linkCopied", { ns: "commons" }),
        type: "info",
      }),
    );
  };

  useEffect(() => {
    if (updateIsSuccess) {
      navigate(`/course/${courseId}`);
    } else if (deleteSuccess || createIsSuccess) {
      navigate("/");
    }
  }, [createIsSuccess, updateIsSuccess, deleteSuccess, courseId, navigate]);

  return (
    <Header
      breadcrumbs={[{ name: isEditing ? t("editCourse") : t("createCourse") }]}
    >
      <div className="flex justify-between">
        <h1 className="mb-4 font-bold text-2xl">
          {isEditing ? t("editCourse") : t("createCourse")}
        </h1>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="form-control w-full">
          <label htmlFor={courseNameId}>
            <span className="font-semibold text-lg">{t("name")}</span>
          </label>
          <input
            type="text"
            id={courseNameId}
            required
            value={course.name}
            onChange={(e) =>
              setCourse((prev) => ({ ...prev, name: e.target.value }))
            }
            className="input w-full text-neutral-content shadow-sm"
          />
        </div>
        <div className="w-full max-w-xs">
          <label htmlFor={aiModelId}>
            <span className="font-semibold text-lg">
              {!isEditing ? t("aiModelEditInfo") : t("aiModel")}
            </span>
          </label>

          <select
            id={aiModelId}
            className="select w-full"
            value={course.modelType}
            onChange={(e) =>
              setCourse((prev) => ({
                ...prev,
                modelType: e.target.value as ModelTypes,
              }))
            }
          >
            {availableModels.map((m) => (
              <option key={m.modelType}>{m.modelType}</option>
            ))}
          </select>
        </div>
        <div className="w-full">
          <label htmlFor={courseDescriptionId}>
            <span className="font-semibold text-lg">{t("description")}</span>
          </label>
          <div id={courseDescriptionInfoId}>{t("descriptionInfo")}</div>
          <textarea
            id={courseDescriptionId}
            aria-describedby={courseDescriptionInfoId}
            value={course.description}
            required
            onChange={(e) =>
              setCourse((prev) => ({ ...prev, description: e.target.value }))
            }
            className="textarea h-40 w-full text-neutral-content shadow-sm"
          />
          <div className="form-control mt-2">
            <label className="label cursor-pointer justify-start gap-2">
              <input
                type="checkbox"
                className="checkbox"
                checked={course.includeDescriptionInChat}
                onChange={(e) =>
                  setCourse((prev) => ({
                    ...prev,
                    includeDescriptionInChat: e.target.checked,
                  }))
                }
              />
              <span
                className={`font-semibold ${course.includeDescriptionInChat ? "text-primary" : "label-text"}`}
              >
                {t("includeDescriptionInChat")}
              </span>
            </label>
          </div>
        </div>
        <PasswordToggle
          isPasswordProtected={!!courseData?.isPasswordProtected}
          course={course}
          setCourse={setCourse}
        />
        <CitationToggle
          citationsEnabled={!!course.citationsEnabled}
          setCourse={setCourse}
        />
        <CourseFeatureToggle
          enabledFeatures={course.enabledFeatures}
          setCourse={setCourse}
        />
        <div className="w-full">
          <label htmlFor={externalLinkId}>
            <span className="font-semibold text-lg">{t("externalLink")}</span>
          </label>
          <input
            type="url"
            id={externalLinkId}
            value={course.extLink || ""}
            onChange={(e) =>
              setCourse((prev) => ({
                ...prev,
                extLink: e.target.value,
              }))
            }
            className="input w-full text-neutral-content shadow-sm"
          />
        </div>
        <div className="w-full">
          <label htmlFor={newManagerId}>
            <span className="font-semibold text-lg">{t("courseManagers")}</span>
          </label>

          <div className="ml-sm">
            <div className="text-md">{t("courseManagersExplanation")}</div>
            <div className="mb-sm flex items-center gap-x-4">
              <span className="font-bold">{`${window.location.origin}/profile`}</span>
              <IconWithTooltip
                className="btn btn-sm btn-outline"
                tooltip={t("copy", { ns: "commons" })}
                icon={<DocumentDuplicateIcon className="h-5 w-5" />}
                handler={handleCopyLink}
              />
            </div>
          </div>

          {course.managerIds.map((managerId) => {
            const isCurrentUser = managerId === user.id;
            const isLecturerOfCourse = managerId === course.lecturerId;
            return (
              <div key={managerId} className="mb-1 ml-sm flex items-center">
                <span className="badge badge-primary p-3 font-mono">
                  {managerId}
                </span>
                {(isAdmin || (!isCurrentUser && !isLecturerOfCourse)) && (
                  <IconWithTooltip
                    className="btn btn-sm btn-circle btn-ghost"
                    icon={<XCircleIcon className="h-6 w-6" />}
                    tooltip={t("removeManager")}
                    handler={() =>
                      setCourse((prev) => ({
                        ...prev,
                        managerIds: prev.managerIds.filter(
                          (id) => id !== managerId,
                        ),
                      }))
                    }
                  />
                )}
                {isCurrentUser && (
                  <span className="ml-2 font-bold text-md">
                    {t("you", { ns: "commons" })}
                  </span>
                )}
                {isLecturerOfCourse && (
                  <span className="ml-2 font-bold text-md">
                    {t("lecturer", { ns: "commons" })}
                  </span>
                )}
              </div>
            );
          })}
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              ref={managerInputRef}
              placeholder={t("enterManagerId")}
              className="input input-bordered w-full"
              id={newManagerId}
              onInput={() => {
                managerInputRef.current?.setCustomValidity("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleAddManager(e);
                }
              }}
            />
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddManager}
            >
              {t("addManager")}
            </button>
          </div>
        </div>
        <div className="form-control w-full max-w-xs">
          <label htmlFor={universityId}>
            <span className="font-semibold text-lg">{t("university")}</span>
          </label>
          <select
            id={universityId}
            className="select w-full"
            value={course.universityId || undefined}
            onChange={(e) => {
              setCourse((prev) => ({
                ...prev,
                universityId: e.target.value || null,
              }));
            }}
          >
            {isAdmin && (
              <option key={"noUniversity"} value={""}>
                {t("noUniversitySelection")}
              </option>
            )}
            {universities?.map((uni) => (
              <option key={uni.id} value={uni.id}>
                {`${uni.fullName} (${uni.shortName})`}
              </option>
            ))}
          </select>
        </div>
        <div className={"collapse-arrow collapse border"}>
          <input type="checkbox" />
          <div className="collapse-title cursor-pointer font-bold">
            {t("advancedSettings")}
          </div>
          <div className="collapse-content">
            <div className="mb-sm flex items-center gap-x-sm rounded border border-warning p-2 text-warning">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span className="text-md">{t("warningAdvancedSettings")}</span>
            </div>
            <label htmlFor={chatPromptId} className="form-control w-full">
              <span className="font-semibold">{t("chatPrompt")}</span>

              <div className="my-1 flex items-center gap-x-sm text-neutral-content/60">
                <ExclamationTriangleIcon className="h-5 w-5" />
                <span className="text-md">{t("warningChatPrompt")}</span>
              </div>

              <textarea
                id={chatPromptId}
                maxLength={MAX_CHAT_PROMPT_LENGTH}
                placeholder={t("placeholderChatPrompt")}
                value={course.chatPrompt || ""}
                onChange={(e) =>
                  setCourse((c) => ({ ...c, chatPrompt: e.target.value }))
                }
                className="textarea w-full text-neutral-content shadow-sm"
              />
            </label>
          </div>
        </div>
        {isAdmin && (
          <div className={"collapse-arrow collapse border"}>
            <input type="checkbox" />
            <div className="collapse-title cursor-pointer font-bold">
              {t("admin")}
            </div>
            <div className="collapse-content">
              <div className="form-control w-full py-2">
                <label htmlFor={lecturerIdInputId}>
                  <span className="font-semibold text-lg">{t("lecturer")}</span>
                </label>
                <input
                  type="text"
                  id={lecturerIdInputId}
                  value={course.lecturerId || ""}
                  onChange={(e) =>
                    setCourse((prev) => ({
                      ...prev,
                      lecturerId: e.target.value,
                    }))
                  }
                  required
                  className="input w-full text-neutral-content shadow-sm"
                />
              </div>

              <div>
                <span className="font-semibold text-lg">
                  {t("isResearchProject")}
                </span>

                <div className="my-sm">
                  <div className="mb-sm flex flex-row items-center">
                    <input
                      type="checkbox"
                      className="toggle mr-md"
                      checked={course.isResearchProject}
                      onChange={(e) => {
                        setCourse((prev) => ({
                          ...prev,
                          isResearchProject: e.target.checked,
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <button
          type="submit"
          className="btn btn-primary shadow-sm"
          disabled={createLoading || updateLoading}
        >
          {isEditing
            ? updateLoading
              ? t("updateLoading")
              : t("updateCourse")
            : createLoading
              ? t("createLoading")
              : t("create")}
        </button>
        {courseId && isAdmin && isEditing && (
          <div>
            <div className="divider divider-error font-bold">
              {t("dangerZone")}
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={openDeleteModal}
                className={`btn btn-error shadow-sm ${deleteLoading ? "loading loading-spinner loading-md" : ""}`}
                disabled={deleteLoading}
              >
                {t("deleteCourse")}
              </button>
              <button
                type="button"
                onClick={openRegenerateModal}
                className="btn btn-error shadow-sm"
              >
                {t("regenerateAllEmbeddings")}
              </button>
            </div>
            <DeleteModal
              title={t("deleteCourse")}
              onConfirm={() => deleteCourse(courseId)}
              onConfirmLabel={t("deleteCourse")}
              confirmVariant={ModalVariant.DESTRUCTIVE}
            >
              {t("confirmDelete")}
            </DeleteModal>
            <RegenerateModal
              title={t("regenerateAllEmbeddings")}
              onConfirm={() => regenerateAllEmbeddings(courseId)}
              onConfirmLabel={t("regenerateAllEmbeddings")}
              confirmVariant={ModalVariant.DESTRUCTIVE}
            >
              {t("regenerateAllEmbeddingsContent")}
            </RegenerateModal>
          </div>
        )}
      </form>
    </Header>
  );
}
